import { IndexRunResult, KBDocument } from '@feedyou/utils'
import { modal } from '@features/imperativeModals'
import React from 'react'
import { knowledgeBaseApi } from '@features/knowledgeBase'
import { css } from 'styled-components'
import { TimeAgo } from '../../../components/Dock/TimeAgo'
import { ModalFooterButton } from '../../../components/modals/styles'
import { feedbotToast } from '../../../lib/feedbotToast'
import { FeedbotThunk } from '../../../store/types'
import { useOpenBotId } from '../../../store/app'

export async function openIndexingRunDetail(
  result: IndexRunResult,
  documents: KBDocument<'raw'>[]
) {
  const {
    failedDocumentIds,
    failedDocumentsCount,
    indexedDocumentsCount,
    cacheSkippedDocumentsCount,
    ignoredDocumentsCount,
    deletedDocumentsCount
  } = result

  const getFilenameForId = (id: string) =>
    documents.find(d => d.id === id)?.file ?? 'Unknown filename'

  const lengthInSeconds = (result.durationMs / 1000).toFixed(0)

  const failedDocsReport = (
    <>
      <br />
      <br />
      The following documents failed to be indexed:
      <ul>
        {failedDocumentIds.map(id => (
          <li>
            {getFilenameForId(id as any)} (id: {id}){' '}
          </li>
        ))}
      </ul>
    </>
  )

  modal(({ onClose }) => {
    return {
      title: (
        <>
          Detail for last indexing run - <TimeAgo timestamp={result.finishedAt} />
        </>
      ),
      children: (
        <div css={bodyCss}>
          Indexing over <b>{result.documentsCount}</b> documents was done in{' '}
          <b>{lengthInSeconds} seconds</b>. <br />
          <br />
          <ul>
            <li>
              <b>{failedDocumentsCount}</b> documents failed indexing
            </li>
            <li>
              <b>{indexedDocumentsCount}</b> documents were indexed
            </li>
            <li>
              <b>{deletedDocumentsCount}</b> documents were deleted
            </li>
            <li>
              <b>{cacheSkippedDocumentsCount}</b> documents were skipped (already indexed and up to
              date)
            </li>
            <li>
              <b>{ignoredDocumentsCount}</b> documents were ignored by settings
            </li>
          </ul>
          <b>{result.chunksCount}</b> chunks were created and <b>{result.embeddingTokensUsage}</b>{' '}
          tokens were used for embedding.
          {failedDocumentsCount > 0 && failedDocsReport}
        </div>
      ),
      modalContentStyle: { height: 'fit-content' },
      modalStyle: { content: { height: 'fit-content', width: 'auto', maxHeight: '94vh' } },
      footer: <ModalFooterButton label="Ok" onClick={() => onClose()} />
    }
  })
}

openIndexingRunDetail.thunk = (modelId: string, botId: string): FeedbotThunk =>
  async function openIndexingRunDetailThunk(dispatch, getState) {
    try {
      const [indexRunsData, documents] = await Promise.all([
        dispatch(
          knowledgeBaseApi.endpoints.getHistoricIndexRuns.initiate({ modelId, botId, count: 1 })
        ).unwrap(),

        dispatch(
          knowledgeBaseApi.endpoints.getKnowledgeBaseDocuments.initiate({ modelId, botId })
        ).unwrap()
      ])

      const lastResult: IndexRunResult | undefined = indexRunsData.runs[0]

      openIndexingRunDetail(lastResult, documents)
    } catch (e) {
      feedbotToast.error({
        title: 'Failed to fetch run result',
        content: "We couldn't fetch the last indexing run result."
      })
    }
  }

const bodyCss = css`
  li {
    list-style: inside;
  }
`

export function useIsLastIndexingRunDetailLoading(modelId: string): boolean {
  const botId = useOpenBotId()!

  const { isLoading: isLoadingRuns } =
    knowledgeBaseApi.endpoints.getHistoricIndexRuns.useQueryState({ modelId, botId, count: 1 })

  const { isLoading: isLoadingDocs } =
    knowledgeBaseApi.endpoints.getKnowledgeBaseDocuments.useQueryState({ modelId, botId })

  return isLoadingRuns || isLoadingDocs
}
